import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';

import { fetchProjectsData } from '../../../../utils/fetchProjectsData';

import { Project, ProjectsData, ProjectStatus } from './projectsData';
import './Projects.css';

const DRIVE_FILE_ID = '1iKL6q8M7StS1nBeEmuG89wfwDpMrp9Ke';

const StatusBadge = ({ status }: { status: ProjectStatus }) => {
  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case 'Completed':
        return 'bg-success';
      case 'InProgress':
        return 'bg-warning';
      case 'NotStarted':
        return 'bg-secondary';
      default:
        return 'bg-light';
    }
  };

  return (
    <span className={`badge ${getStatusColor(status)} status-badge`}>
      {status}
    </span>
  );
};

const ProjectCard = ({ project }: { project: Project }) => (
  <div className="project-card">
    <div className="project-header">
      <h3 className="project-title">{project.title}</h3>
      <StatusBadge status={project.status} />
    </div>
    <p className="project-description">{project.description}</p>
    {project.link && (
      <div className="project-footer">
        <Link to={project.link} className="more-details-link">
          More Details <span className="arrow">→</span>
        </Link>
      </div>
    )}
  </div>
);

const MinorProjectCard = ({ project }: { project: Project }) => (
  <div className="minor-project-card">
    <div className="minor-project-content">
      <span className="minor-project-title">{project.title}</span>
      <StatusBadge status={project.status} />
    </div>
    <p className="minor-project-description">{project.description}</p>
  </div>
);

const ProjectCategory = ({
  title,
  projects,
  isMinor = false,
}: {
  title: string;
  projects: Project[];
  isMinor?: boolean;
}) => (
  <section className="project-category">
    <h2 className="category-title">{title}</h2>
    <div className={isMinor ? 'minor-project-grid' : 'project-grid'}>
      {projects.map((project) =>
        isMinor ? (
          <MinorProjectCard key={project.id} project={project} />
        ) : (
          <ProjectCard key={project.id} project={project} />
        )
      )}
    </div>
  </section>
);

const Projects = () => {
  const [projectsData, setProjectsData] = useState<ProjectsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProjectsData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchProjectsData(DRIVE_FILE_ID);
        // Ensure data is ProjectsData type and has a projects array
        if (data && Array.isArray(data.projects)) {
          setProjectsData(data);
        } else {
          setProjectsData({ projects: [] });
        }
      } catch (error) {
        console.error('Failed to load projects data:', error);
        setProjectsData({ projects: [] });
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectsData();
  }, []);

  const projects = projectsData?.projects ?? [];
  const majorProjects = projects.filter((p) => p.category === 'Major');
  const moderateProjects = projects.filter((p) => p.category === 'Moderate');
  const minorProjects = projects.filter((p) => p.category === 'Minor');

  return (
    <div className="projects-container">
      <header className="projects-header">
        <h1>Community Projects</h1>
        <p>Tracking our community's ongoing and completed initiatives</p>
      </header>

      {isLoading ? (
        <div className="text-center my-5">
          <BeatLoader color="#0d6efd" size={15} margin={2} />
        </div>
      ) : (
        <div className="projects-content">
          <ProjectCategory title="Major Projects" projects={majorProjects} />
          <ProjectCategory
            title="Other Projects"
            projects={minorProjects}
            isMinor={true}
          />
        </div>
      )}
    </div>
  );
};

export default Projects;
